// IMPORTS

// external
import algosdk, { Algodv2, Transaction, encodeUint64, decodeUint64 } from "algosdk"

// global
import { FIXED_3_SCALE_FACTOR, TEXT_ENCODER, PERMISSIONLESS_SENDER_LOGIC_SIG } from "../../globals"
import { getLocalStates, getAccountBalances, getAccountMinBalance, storeTransferDetails } from "../../stateUtils"
import { getParams } from "../../transactionUtils"
import { decodeBytes, parseAddressBytes } from "../../utils"
import ParsedTransaction from "../../parsedTransaction"
import { Base64Encoder } from "../../encoder"

// local
import { MANAGER_STRINGS, MARKET_STRINGS, MarketType } from "./lendingConfig"
import LendingClient from "./lendingClient"
import Market from "./market"
import UserMarketState from "./userMarketState"

// interface

export default class User {
  public algod: Algodv2
  public address: string
  public storageAddress: string
  public lendingClient: LendingClient

  public storageBalances = {}
  public storageMinBalance: number

  public optedInToManager = false
  public optedInMarkets = []
  public userMarketStates: { [key: number]: UserMarketState } = {}

  // net values
  public netSupplied: number
  public netScaledCollateral: number
  public netSupplyAPR: number
  public netBorrowed: number
  public netScaledBorrow: number
  public netBorrowAPR: number
  public netUnclaimedRewards = {}
  public netRewardsPerYear = {}
  public netSupplyRewardsPerYear: number
  public netBorrowRewardsPerYear: number
  public netSupplyRewardsAPR: number
  public netBorrowRewardsAPR: number

  /**
   * Constructor for the lending user class.
   * 
   * @param lendingClient - lending client
   * @param address - address for user
   */
  constructor(lendingClient: LendingClient, address: string) {
    this.lendingClient = lendingClient
    this.algod = this.lendingClient.algod
    this.address = address
  }

  /**
   * Functino which updates the lending user object to match the user's actual
   * lending local state.
   * 
   * @param userLocalStates - a list of all of the user's local states
   */
  async loadState(userLocalStates: {}) {
    // parse user state
    if (this.lendingClient.manager.appId in userLocalStates) {
      this.optedInToManager = true
      this.storageAddress = parseAddressBytes(
        userLocalStates[this.lendingClient.manager.appId][MANAGER_STRINGS.storage_account]
      )

      // reset state
      this.optedInMarkets = []
      this.userMarketStates = {}

      // load storage balances
      this.storageBalances = await getAccountBalances(this.algod, this.storageAddress)
      this.storageMinBalance = await getAccountMinBalance(this.algod, this.storageAddress)

      // load storage state
      let storageLocalStates = await getLocalStates(this.algod, this.storageAddress)

      // load manager state
      let managerState = storageLocalStates[this.lendingClient.manager.appId]
      for (var i = 0; i < 7; ++i) {
        let pageKey = MANAGER_STRINGS.opted_in_markets_page_prefix + String.fromCharCode.apply(null, encodeUint64(i))
        if (pageKey in managerState) {
          let pageBytes = Buffer.from(managerState[pageKey], "base64").toString("binary")
          let pageItems = Math.floor(pageBytes.length / 8)
          for (var j = 0; j < pageItems; ++j) {
            this.optedInMarkets.push(decodeUint64(decodeBytes(pageBytes.substr(j * 8, 8)), "safe"))
          }
        }
      }

      // load market states
      this.optedInMarkets.forEach(marketAppId => {
        this.userMarketStates[marketAppId] = new UserMarketState(
          storageLocalStates[marketAppId],
          this.lendingClient.markets[marketAppId]
        )
      })

      // calc net values
      this.netSupplied = 0
      this.netScaledCollateral = 0
      this.netBorrowed = 0
      this.netScaledBorrow = 0
      this.netUnclaimedRewards = {}
      this.netRewardsPerYear = {}
      this.netSupplyRewardsPerYear = 0
      this.netBorrowRewardsPerYear = 0
      let dollarTotaledSupplyAPR = 0
      let dollarTotaledBorrowAPR = 0
      for (const [key, value] of Object.entries(this.userMarketStates)) {
        let market = this.lendingClient.markets[key]
        // TODO improve efficiency
        this.netSupplied += value.suppliedAmount.toUSD()
        this.netBorrowed += value.borrowedAmount.toUSD()
        this.netScaledCollateral += Number(((value.suppliedAmount.toUSD() * market.collateralFactor) / FIXED_3_SCALE_FACTOR).toFixed(3))
        if (value.borrowShares != 0) { // round up unless exactly 0
          this.netScaledBorrow += Number(((value.borrowedAmount.toUSD() * market.borrowFactor) / FIXED_3_SCALE_FACTOR).toFixed(3)) + 0.001
        }
        dollarTotaledSupplyAPR += value.suppliedAmount.toUSD() * (await market.getSupplyAPR())
        dollarTotaledBorrowAPR += value.borrowedAmount.toUSD() * market.getBorrowAPR()
        
        // rewards
        for (var i = 0; i < 2; i++) {
          let assetId = value.rewardsProgramStates[i].assetID
          if (assetId > 0) {
            this.netUnclaimedRewards[assetId] =
              value.rewardsProgramStates[i].realUnclaimed + (this.netUnclaimedRewards[assetId] || 0)
            this.netRewardsPerYear[assetId] =
              value.rewardsProgramStates[i].rewardsPerYear + (this.netRewardsPerYear[assetId] || 0)
            if (market.marketType == MarketType.VAULT ||  market.marketType == MarketType.LP) {
              this.netSupplyRewardsPerYear += this.lendingClient.algofiClient.assetData.getAsset(
                value.rewardsProgramStates[i].rewardsPerYear, assetId).toUSD()
            } else {
              this.netBorrowRewardsPerYear += this.lendingClient.algofiClient.assetData.getAsset(
                value.rewardsProgramStates[i].rewardsPerYear, assetId).toUSD()
            }
          }
        }
      }
      
      if (this.netSupplied > 0) {
        this.netSupplyAPR = dollarTotaledSupplyAPR / this.netSupplied
        this.netSupplyRewardsAPR = this.netSupplyRewardsPerYear / this.netSupplied
      } else {
        this.netSupplyAPR = 0
        this.netSupplyRewardsAPR = 0
      }
      
      if (this.netBorrowed > 0) {
        this.netBorrowAPR = dollarTotaledBorrowAPR / this.netBorrowed
        this.netBorrowRewardsAPR = this.netBorrowRewardsPerYear / this.netBorrowed
      } else {
        this.netBorrowAPR = 0
        this.netBorrowRewardsAPR = 0
      }
    } else {
      this.optedInToManager = false
    }
  }

  /**
   * Returns whether or not the user is opted into the market.
   * 
   * @param marketAppId - application id of the market
   * @returns whether or not the user is opted into the market.
   */
  isUserOptedIntoMarket(marketAppId: number): boolean {
    return marketAppId in this.userMarketStates
  }

  /**
   * Returns page offset for a market.
   * 
   * @param marketAppId - application id of the market
   * @returns an array of the page and offset that the market is stored at.
   */
  getMarketPageOffset(marketAppId: number): [number, number] {
    let marketIndex = this.optedInMarkets.indexOf(marketAppId)
    let page = Math.floor(marketIndex / 3)
    let offset = marketIndex % 3
    return [page, offset]
  }

  /**
   * Constructs a series of transactions that calculate a user's positions in a market.
   * 
   * @param targetMarketAppId - an instance of an algofi client
   * @returns a series of transactions that calculate a user's positions in a market.
   */
  async getCalcUserPositionTransactions(targetMarketAppId: number): Promise<Transaction[]> {
    let transactions: Transaction[] = []

    const params = await getParams(this.algod)
    params.fee = 0

    let pageCount = Math.ceil(this.optedInMarkets.length / 3)
    for (var page = 0; page < pageCount; ++page) {
      let marketsOnPage = (page + 1) * 3 <= this.optedInMarkets.length ? 3 : this.optedInMarkets.length % 3

      let foreignApps = []
      for (var offset = 0; offset < marketsOnPage; ++offset) {
        let market = this.lendingClient.markets[this.optedInMarkets[page * 3 + offset]]
        foreignApps.push(market.appId)
        foreignApps.push(market.oracle.appId)
      }

      let txn = algosdk.makeApplicationNoOpTxnFromObject({
        from: PERMISSIONLESS_SENDER_LOGIC_SIG.lsig.address(),
        appIndex: this.lendingClient.manager.appId,
        suggestedParams: params,
        appArgs: [
          TEXT_ENCODER.encode(MANAGER_STRINGS.calculate_user_position),
          encodeUint64(page),
          encodeUint64(targetMarketAppId)
        ],
        accounts: [this.storageAddress],
        foreignApps: foreignApps,
        foreignAssets: undefined,
        rekeyTo: undefined
      })

      transactions.push(txn)
    }

    return transactions
  }

  parseTransaction(txns: {}[], txnIdx: number, parsedTransactions: ParsedTransaction[]) {
    const txn = txns[txnIdx]
    const nextTxn = txns[txnIdx + 1]
    let appId = txn['application-transaction']['application-id']

    let assetsIn = {}
    let assetsOut = {}

    if (appId == this.lendingClient.manager.appId) { // manager
      let command = Base64Encoder.decode(txn['application-transaction']['application-args'][0])
      switch(command) { // TODO implement gov and vault functions and manager close out
        case MANAGER_STRINGS.user_opt_in:
          {
            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "PROTOCOL OPT IN", [], assetsIn, assetsOut)
            )

            break
          }
        case MANAGER_STRINGS.user_market_opt_in:
          {
            let marketAppId = txn['application-transaction']['foreign-apps'][0]

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", marketAppId, "MARKET OPT IN", [], assetsIn, assetsOut)
            )

            break
          }
        case MANAGER_STRINGS.user_market_close_out:
          {
            let marketAppId = txn['application-transaction']['foreign-apps'][0]

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", marketAppId, "MARKET OPT OUT", [], assetsIn, assetsOut)
            )

            break
          }
        default:
          return
      }

    } else if (appId in this.lendingClient.markets) { // markets
      let command = Base64Encoder.decode(txn['application-transaction']['application-args'][0])
      switch(command) { // add support for vault txns
        case MARKET_STRINGS.mint_b_asset:
          {
            if (!nextTxn) { return }; // skip, transfer not included

            storeTransferDetails(nextTxn, assetsIn)
            storeTransferDetails(txn['inner-txns'][1], assetsOut)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "MINT B ASSET", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.add_underlying_collateral:
          {
            if (!nextTxn) { return }; // skip, transfer not included

            storeTransferDetails(nextTxn, assetsIn)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "ADD COLLATERAL", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.add_b_asset_collateral:
          {
            if (!nextTxn) { return }; // skip, transfer not included

            storeTransferDetails(nextTxn, assetsIn)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "ADD B ASSET", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.remove_underlying_collateral:
          {
            let assetOut = 0
            let amountOut = 0

            if (this.lendingClient.markets[appId].marketType == MarketType.VAULT) {
              storeTransferDetails(txn['inner-txns'][1]['inner-txns'][0], assetsOut)
            } else {
              storeTransferDetails(txn['inner-txns'][1], assetsOut)
            }

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "REMOVE COLLATERAL", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.remove_b_asset_collateral:
          {

            storeTransferDetails(txn['inner-txns'][1], assetsOut)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "REMOVE B ASSET", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.burn_b_asset:
          {
            if (!nextTxn) { return }; // skip, transfer not included

            storeTransferDetails(nextTxn, assetsIn)
            storeTransferDetails(txn['inner-txns'][1], assetsOut)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "BURN", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.borrow:
          {
            storeTransferDetails(txn['inner-txns'][1], assetsOut)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "BORROW", [], assetsIn, assetsOut)
            )
            break
          }
        case MARKET_STRINGS.repay_borrow:
          {
            if (!nextTxn) { return }; // skip, transfer not included

            storeTransferDetails(nextTxn, assetsIn)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "REPAY BORROW", [], assetsIn, assetsOut)
            )

            break
          }
        case MARKET_STRINGS.claim_rewards:
          {
            storeTransferDetails(txn['inner-txns'][1], assetsOut)

            parsedTransactions.push(
              new ParsedTransaction(txn, "LENDING v2", appId, "CLAIM REWARDS", [], assetsIn, assetsOut)
            )

            break
          }
        default:
          return
      }
    }
  }
  
}
